'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { FileText, Plus, Search, Download, Eye, X, ChevronRight, Building2, Truck, Package, CheckCircle2, XCircle, Star, Filter, Clock,  } from 'lucide-react';
import { MOCK_QUOTATIONS, MOCK_RFQS, SUPPLIERS, QUOTATION_STATUS_STYLES, formatUGX, Quotation, QuotationStatus, QuotationItem, RFQ,  } from '../../components/procurementData';
import { useRole } from '@/lib/useRole';

// ─── Status Badge ─────────────────────────────────────────────────────────────
function QuotationStatusBadge({ status }: { status: QuotationStatus }) {
  const st = QUOTATION_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

// ─── Compliance Badge ─────────────────────────────────────────────────────────
function ComplianceBadge({ compliance }: { compliance: QuotationItem['specificationsCompliance'] }) {
  const map = {
    full: { label: 'Full', cls: 'bg-green-50 text-green-700' },
    partial: { label: 'Partial', cls: 'bg-amber-50 text-amber-700' },
    non_compliant: { label: 'Non-Compliant', cls: 'bg-red-50 text-red-700' },
  };
  const s = map[compliance];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

// ─── Quotation Detail Drawer ──────────────────────────────────────────────────
function QuotationDrawer({ quot, onClose, onAccept, onReject, onShortlist }: {
  quot: Quotation;
  onClose: () => void;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onShortlist: (id: string) => void;
}) {
  const { can } = useRole();
  const canManage = can('manage_procurement');
  const rfq = MOCK_RFQS.find(r => r.id === quot.rfqId);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{quot.quotationNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{quot.supplierName}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Status & RFQ Link */}
          <div className="flex items-center gap-2 flex-wrap">
            <QuotationStatusBadge status={quot.status} />
            <Link href="/procurement/rfqs" className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">
              <FileText size={10} />
              {quot.rfqNumber}
            </Link>
          </div>

          {/* Supplier Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">{quot.supplierName}</div>
                {(() => {
                  const sup = SUPPLIERS.find(s => s.id === quot.supplierId);
                  return sup ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{sup.code}</span>
                      <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold">
                        <Star size={10} fill="currentColor" /> {sup.rating}
                      </span>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Quotation Date', value: quot.quotationDate },
              { label: 'Valid Until', value: quot.validityDate },
              { label: 'Currency', value: quot.currency },
              { label: 'Payment Terms', value: quot.paymentTerms },
              { label: 'Delivery Period', value: quot.deliveryPeriod },
              { label: 'Warranty', value: quot.warranty || 'N/A' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Line Items */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <Package size={12} /> Quoted Items
            </div>
            <div className="space-y-2">
              {quot.items.map(item => (
                <div key={item.id} className="border border-gray-100 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">{item.description}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.unit}{item.brand ? ` · ${item.brand}` : ''}</div>
                    </div>
                    <ComplianceBadge compliance={item.specificationsCompliance} />
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <div className="text-gray-400">Qty</div>
                      <div className="font-semibold text-gray-700">{item.quantity}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Unit Price</div>
                      <div className="font-semibold text-gray-700">{formatUGX(item.unitPrice)}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Discount</div>
                      <div className="font-semibold text-gray-700">{item.discount}%</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Total</div>
                      <div className="font-bold text-gray-900">{formatUGX(item.totalPrice)}</div>
                    </div>
                  </div>
                  {item.deliveryTime && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <Clock size={10} /> Delivery: {item.deliveryTime}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Financial Summary</div>
            {[
              { label: 'Subtotal', value: quot.subtotal },
              { label: 'Discount', value: -quot.discount, negative: true },
              { label: 'Tax', value: quot.tax },
              { label: 'Delivery Charges', value: quot.deliveryCharges },
            ].map(row => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-gray-500">{row.label}</span>
                <span className={`font-medium ${row.negative && row.value < 0 ? 'text-green-600' : 'text-gray-700'}`}>
                  {row.negative && quot.discount > 0 ? `- ${formatUGX(quot.discount)}` : formatUGX(Math.abs(row.value))}
                </span>
              </div>
            ))}
            <div className="border-t border-gray-200 pt-2 flex justify-between">
              <span className="text-sm font-bold text-gray-900">Grand Total</span>
              <span className="text-base font-bold text-orange-600">{formatUGX(quot.grandTotal)}</span>
            </div>
          </div>

          {/* Notes */}
          {quot.notes && (
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Supplier Notes</div>
              <div className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{quot.notes}</div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {canManage && (
          <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-wrap">
            {quot.status === 'received' && (
              <button
                onClick={() => onShortlist(quot.id)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors"
              >
                <Star size={14} /> Shortlist
              </button>
            )}
            {(quot.status === 'received' || quot.status === 'shortlisted' || quot.status === 'under_review') && (
              <>
                <button
                  onClick={() => onAccept(quot.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors"
                >
                  <CheckCircle2 size={14} /> Accept
                </button>
                <button
                  onClick={() => onReject(quot.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors"
                >
                  <XCircle size={14} /> Reject
                </button>
              </>
            )}
            <button onClick={onClose} className="ml-auto px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── New Quotation Form ───────────────────────────────────────────────────────
function NewQuotationForm({ onClose, onSave, defaultRfqId }: {
  onClose: () => void;
  onSave: (q: Quotation) => void;
  defaultRfqId?: string;
}) {
  const [form, setForm] = useState({
    rfqId: defaultRfqId || '',
    supplierId: '',
    quotationDate: new Date().toISOString().split('T')[0],
    validityDate: '',
    paymentTerms: 'Net 30',
    deliveryPeriod: '',
    deliveryCharges: 0,
    tax: 0,
    discount: 0,
    warranty: '',
    notes: '',
    currency: 'UGX',
  });
  const [items, setItems] = useState<QuotationItem[]>([
    { id: 'qi-new-1', description: '', unit: 'bag', quantity: 1, unitPrice: 0, discount: 0, tax: 0, totalPrice: 0, deliveryTime: '', brand: '', specificationsCompliance: 'full' },
  ]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedRFQ = MOCK_RFQS.find(r => r.id === form.rfqId);

  const handleRFQChange = (rfqId: string) => {
    setForm(f => ({ ...f, rfqId, supplierId: '' }));
    const rfq = MOCK_RFQS.find(r => r.id === rfqId);
    if (rfq) {
      setItems(rfq.items.map((item, i) => ({
        id: `qi-new-${i + 1}`,
        description: item.description,
        unit: item.unit,
        quantity: item.quantity,
        unitPrice: 0,
        discount: 0,
        tax: 0,
        totalPrice: 0,
        deliveryTime: '',
        brand: '',
        specificationsCompliance: 'full' as const,
      })));
    }
  };

  const calcItemTotal = (item: QuotationItem) => {
    const base = item.quantity * item.unitPrice;
    const afterDiscount = base * (1 - item.discount / 100);
    const withTax = afterDiscount * (1 + item.tax / 100);
    return Math.round(withTax);
  };

  const updateItem = (id: string, field: keyof QuotationItem, value: string | number) => {
    setItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const updated = { ...i, [field]: value };
      updated.totalPrice = calcItemTotal(updated);
      return updated;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, { id: `qi-new-${Date.now()}`, description: '', unit: 'bag', quantity: 1, unitPrice: 0, discount: 0, tax: 0, totalPrice: 0, deliveryTime: '', brand: '', specificationsCompliance: 'full' }]);
  };

  const removeItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id));

  const subtotal = items.reduce((s, i) => s + i.totalPrice, 0);
  const grandTotal = subtotal - form.discount + form.tax + form.deliveryCharges;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.rfqId) e.rfqId = 'Select an RFQ';
    if (!form.supplierId) e.supplierId = 'Select a supplier';
    if (!form.validityDate) e.validityDate = 'Validity date is required';
    if (!form.deliveryPeriod) e.deliveryPeriod = 'Delivery period is required';
    if (items.some(i => !i.description.trim())) e.items = 'All items must have a description';
    if (items.some(i => i.unitPrice <= 0)) e.itemsPrice = 'All unit prices must be greater than zero';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const sup = SUPPLIERS.find(s => s.id === form.supplierId)!;
    const rfq = MOCK_RFQS.find(r => r.id === form.rfqId)!;
    const newQuot: Quotation = {
      id: `quot-${Date.now()}`,
      quotationNumber: `QT-${sup.code}-${Date.now().toString().slice(-6)}`,
      supplierId: form.supplierId,
      supplierName: sup.name,
      rfqId: form.rfqId,
      rfqNumber: rfq.rfqNumber,
      quotationDate: form.quotationDate,
      validityDate: form.validityDate,
      currency: form.currency,
      paymentTerms: form.paymentTerms,
      deliveryPeriod: form.deliveryPeriod,
      deliveryCharges: form.deliveryCharges,
      tax: form.tax,
      discount: form.discount,
      subtotal,
      grandTotal,
      warranty: form.warranty,
      notes: form.notes,
      status: 'received',
      items,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(newQuot);
  };

  // Suppliers available for selected RFQ
  const availableSuppliers = selectedRFQ
    ? selectedRFQ.suppliers.map(s => SUPPLIERS.find(sup => sup.id === s.supplierId)).filter(Boolean)
    : SUPPLIERS;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">Record Supplier Quotation</div>
            <div className="text-xs text-gray-400 mt-0.5">Enter pricing, tax, and delivery terms from supplier</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-6">
          {/* RFQ Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">RFQ <span className="text-red-500">*</span></label>
              <select
                value={form.rfqId}
                onChange={e => handleRFQChange(e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${errors.rfqId ? 'border-red-400' : 'border-gray-200'}`}
              >
                <option value="">— Select RFQ —</option>
                {MOCK_RFQS.filter(r => ['issued', 'open'].includes(r.status)).map(r => (
                  <option key={r.id} value={r.id}>{r.rfqNumber} · {r.farm}</option>
                ))}
              </select>
              {errors.rfqId && <p className="text-xs text-red-500 mt-1">{errors.rfqId}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Supplier <span className="text-red-500">*</span></label>
              <select
                value={form.supplierId}
                onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))}
                className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${errors.supplierId ? 'border-red-400' : 'border-gray-200'}`}
              >
                <option value="">— Select Supplier —</option>
                {availableSuppliers.map(s => s && (
                  <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                ))}
              </select>
              {errors.supplierId && <p className="text-xs text-red-500 mt-1">{errors.supplierId}</p>}
            </div>
          </div>

          {/* Dates & Terms */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quotation Date</label>
              <input type="date" value={form.quotationDate} onChange={e => setForm(f => ({ ...f, quotationDate: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Valid Until <span className="text-red-500">*</span></label>
              <input type="date" value={form.validityDate} onChange={e => setForm(f => ({ ...f, validityDate: e.target.value }))} className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.validityDate ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.validityDate && <p className="text-xs text-red-500 mt-1">{errors.validityDate}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Payment Terms</label>
              <select value={form.paymentTerms} onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                {['Net 14', 'Net 30', 'Net 60', 'Cash on Delivery', 'Advance Payment', '50% Advance'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Delivery Period <span className="text-red-500">*</span></label>
              <input type="text" placeholder="e.g. 3 working days" value={form.deliveryPeriod} onChange={e => setForm(f => ({ ...f, deliveryPeriod: e.target.value }))} className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.deliveryPeriod ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.deliveryPeriod && <p className="text-xs text-red-500 mt-1">{errors.deliveryPeriod}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Currency</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                <option>UGX</option><option>USD</option><option>KES</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Warranty</label>
              <input type="text" placeholder="e.g. 12 months" value={form.warranty} onChange={e => setForm(f => ({ ...f, warranty: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">Quoted Items <span className="text-red-500">*</span></label>
              <button onClick={addItem} className="flex items-center gap-1 text-xs text-orange-600 font-semibold hover:text-orange-700">
                <Plus size={12} /> Add Item
              </button>
            </div>
            {(errors.items || errors.itemsPrice) && <p className="text-xs text-red-500 mb-2">{errors.items || errors.itemsPrice}</p>}
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={item.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-500">Item {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-orange-600">{formatUGX(item.totalPrice)}</span>
                      {items.length > 1 && (
                        <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                      )}
                    </div>
                  </div>
                  <input type="text" placeholder="Item description *" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Brand (optional)" value={item.brand || ''} onChange={e => updateItem(item.id, 'brand', e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    <input type="text" placeholder="Delivery time (e.g. 3 days)" value={item.deliveryTime || ''} onChange={e => updateItem(item.id, 'deliveryTime', e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-xs text-gray-400 mb-0.5 block">Qty</label>
                      <input type="number" min={1} value={item.quantity} onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))} className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-0.5 block">Unit Price</label>
                      <input type="number" min={0} value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', Number(e.target.value))} className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-0.5 block">Disc %</label>
                      <input type="number" min={0} max={100} value={item.discount} onChange={e => updateItem(item.id, 'discount', Number(e.target.value))} className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-0.5 block">Tax %</label>
                      <input type="number" min={0} value={item.tax} onChange={e => updateItem(item.id, 'tax', Number(e.target.value))} className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-0.5 block">Spec Compliance</label>
                    <select value={item.specificationsCompliance} onChange={e => updateItem(item.id, 'specificationsCompliance', e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                      <option value="full">Full Compliance</option>
                      <option value="partial">Partial Compliance</option>
                      <option value="non_compliant">Non-Compliant</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charges */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Delivery Charges</label>
              <input type="number" min={0} value={form.deliveryCharges} onChange={e => setForm(f => ({ ...f, deliveryCharges: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tax (UGX)</label>
              <input type="number" min={0} value={form.tax} onChange={e => setForm(f => ({ ...f, tax: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Discount (UGX)</label>
              <input type="number" min={0} value={form.discount} onChange={e => setForm(f => ({ ...f, discount: Number(e.target.value) }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>

          {/* Totals Preview */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span className="font-medium">{formatUGX(subtotal)}</span></div>
            {form.discount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-600">Discount</span><span className="font-medium text-green-600">- {formatUGX(form.discount)}</span></div>}
            {form.tax > 0 && <div className="flex justify-between text-sm"><span className="text-gray-600">Tax</span><span className="font-medium">{formatUGX(form.tax)}</span></div>}
            {form.deliveryCharges > 0 && <div className="flex justify-between text-sm"><span className="text-gray-600">Delivery</span><span className="font-medium">{formatUGX(form.deliveryCharges)}</span></div>}
            <div className="border-t border-orange-200 pt-2 flex justify-between">
              <span className="text-sm font-bold text-gray-900">Grand Total</span>
              <span className="text-base font-bold text-orange-600">{formatUGX(grandTotal)}</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes from supplier..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button onClick={handleSave} className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors">
            <FileText size={14} /> Record Quotation
          </button>
          <button onClick={onClose} className="ml-auto px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function QuotationsContent({ defaultRfqId }: { defaultRfqId?: string }) {
  const { can } = useRole();
  const canManage = can('manage_procurement');
  const canView = can('view_procurement');

  const [quotations, setQuotations] = useState<Quotation[]>(MOCK_QUOTATIONS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'all'>('all');
  const [rfqFilter, setRfqFilter] = useState<string>(defaultRfqId || 'all');
  const [selectedQuot, setSelectedQuot] = useState<Quotation | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  const filtered = useMemo(() => {
    return quotations.filter(q => {
      const matchSearch = q.quotationNumber.toLowerCase().includes(search.toLowerCase()) ||
        q.supplierName.toLowerCase().includes(search.toLowerCase()) ||
        q.rfqNumber.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || q.status === statusFilter;
      const matchRFQ = rfqFilter === 'all' || q.rfqId === rfqFilter;
      return matchSearch && matchStatus && matchRFQ;
    });
  }, [quotations, search, statusFilter, rfqFilter]);

  const handleAccept = (id: string) => {
    setQuotations(prev => prev.map(q => q.id === id ? { ...q, status: 'accepted' as QuotationStatus } : q));
    setSelectedQuot(prev => prev?.id === id ? { ...prev, status: 'accepted' as QuotationStatus } : prev);
  };

  const handleReject = (id: string) => {
    setQuotations(prev => prev.map(q => q.id === id ? { ...q, status: 'rejected' as QuotationStatus } : q));
    setSelectedQuot(prev => prev?.id === id ? { ...prev, status: 'rejected' as QuotationStatus } : prev);
  };

  const handleShortlist = (id: string) => {
    setQuotations(prev => prev.map(q => q.id === id ? { ...q, status: 'shortlisted' as QuotationStatus } : q));
    setSelectedQuot(prev => prev?.id === id ? { ...prev, status: 'shortlisted' as QuotationStatus } : prev);
  };

  const handleSaveNew = (q: Quotation) => {
    setQuotations(prev => [q, ...prev]);
    setShowNewForm(false);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: quotations.length };
    quotations.forEach(q => { counts[q.status] = (counts[q.status] || 0) + 1; });
    return counts;
  }, [quotations]);

  if (!canView) {
    return (
      <div className="p-8 text-center">
        <FileText size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Access Restricted</h2>
        <p className="text-gray-500">You do not have permission to view quotations.</p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link href="/procurement" className="text-gray-400 hover:text-gray-600 transition-colors">
              <ChevronRight size={16} className="rotate-180" />
            </Link>
            <FileText size={22} className="text-orange-600" />
            <h1 className="text-2xl font-bold text-gray-900">Supplier Quotations</h1>
          </div>
          <p className="text-sm text-gray-500 max-w-xl">
            Record and manage supplier quotations linked to RFQs. Compare pricing, tax, delivery terms, and specification compliance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/procurement/rfqs" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <FileText size={14} /> View RFQs
          </Link>
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Download size={14} /> Export
          </button>
          {canManage && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm"
            >
              <Plus size={15} /> Record Quotation
            </button>
          )}
        </div>
      </div>

      {/* Status KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {([
          { label: 'All Quotations', key: 'all', color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: 'Received', key: 'received', color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Shortlisted', key: 'shortlisted', color: 'text-teal-700', bg: 'bg-teal-50' },
          { label: 'Accepted', key: 'accepted', color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Rejected', key: 'rejected', color: 'text-red-700', bg: 'bg-red-50' },
        ] as const).map(s => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key as QuotationStatus | 'all')}
            className={`p-3 rounded-2xl border text-left transition-all ${statusFilter === s.key ? `${s.bg} border-current ring-2 ring-offset-1 ring-orange-400` : 'bg-white border-gray-100 hover:border-gray-200'}`}
          >
            <div className={`text-2xl font-bold ${s.color}`}>{statusCounts[s.key] || 0}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by quotation number, supplier, RFQ..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select
            value={rfqFilter}
            onChange={e => setRfqFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="all">All RFQs</option>
            {MOCK_RFQS.map(r => <option key={r.id} value={r.id}>{r.rfqNumber}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as QuotationStatus | 'all')}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="all">All Statuses</option>
            {(['received', 'under_review', 'shortlisted', 'accepted', 'rejected'] as QuotationStatus[]).map(s => (
              <option key={s} value={s}>{QUOTATION_STATUS_STYLES[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <div className="text-base font-semibold text-gray-600 mb-1">No quotations found</div>
            <div className="text-sm text-gray-400">
              {search || statusFilter !== 'all' || rfqFilter !== 'all' ?'Try adjusting your search or filters.' :'No quotations have been received yet. Record the first supplier quotation.'}
            </div>
            {canManage && !search && statusFilter === 'all' && rfqFilter === 'all' && (
              <button onClick={() => setShowNewForm(true)} className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors mx-auto">
                <Plus size={14} /> Record Quotation
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Quotation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">RFQ</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date / Valid Until</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment / Delivery</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Grand Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(quot => {
                  const sup = SUPPLIERS.find(s => s.id === quot.supplierId);
                  const isExpired = new Date(quot.validityDate) < new Date();
                  return (
                    <tr key={quot.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{quot.quotationNumber}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{quot.items.length} item{quot.items.length !== 1 ? 's' : ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{quot.supplierName}</div>
                        {sup && (
                          <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
                            <Star size={10} fill="currentColor" /> {sup.rating}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{quot.rfqNumber}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{quot.quotationDate}</div>
                        <div className={`text-xs mt-0.5 ${isExpired ? 'text-red-500' : 'text-gray-400'}`}>
                          {isExpired ? '⚠ Expired' : `Valid: ${quot.validityDate}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{quot.paymentTerms}</div>
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Truck size={10} /> {quot.deliveryPeriod}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-gray-900">{formatUGX(quot.grandTotal)}</div>
                        {quot.discount > 0 && <div className="text-xs text-green-600 mt-0.5">- {formatUGX(quot.discount)} disc.</div>}
                      </td>
                      <td className="px-4 py-3">
                        <QuotationStatusBadge status={quot.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedQuot(quot)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                          {canManage && quot.status === 'received' && (
                            <>
                              <button onClick={() => handleShortlist(quot.id)} className="p-1.5 rounded-lg hover:bg-teal-50 transition-colors text-teal-500 hover:text-teal-700" title="Shortlist">
                                <Star size={14} />
                              </button>
                              <button onClick={() => handleAccept(quot.id)} className="p-1.5 rounded-lg hover:bg-green-50 transition-colors text-green-500 hover:text-green-700" title="Accept">
                                <CheckCircle2 size={14} />
                              </button>
                              <button onClick={() => handleReject(quot.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-400 hover:text-red-600" title="Reject">
                                <XCircle size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="mt-3 text-xs text-gray-400 text-right">
          Showing {filtered.length} of {quotations.length} quotation{quotations.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Detail Drawer */}
      {selectedQuot && (
        <QuotationDrawer
          quot={selectedQuot}
          onClose={() => setSelectedQuot(null)}
          onAccept={handleAccept}
          onReject={handleReject}
          onShortlist={handleShortlist}
        />
      )}

      {/* New Quotation Form */}
      {showNewForm && (
        <NewQuotationForm
          onClose={() => setShowNewForm(false)}
          onSave={handleSaveNew}
          defaultRfqId={defaultRfqId}
        />
      )}
    </div>
  );
}
